import { useMetaPlatform, useHealthCheck } from '../hooks/usePlatforms';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';

export function Settings() {
  const { data: metaPlatform, isLoading, error, refetch } = useMetaPlatform();
  const { data: health } = useHealthCheck();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        title="Failed to load settings"
        message={error.message}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your AppEdge meta-platform configuration
        </p>
      </div>

      {/* Meta Platform Info */}
      {metaPlatform && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Meta-Platform Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Core configuration and settings
            </p>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {metaPlatform.name}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Slug</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {metaPlatform.slug}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Billing Mode</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {metaPlatform.billing_mode}
                  </span>
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Data Isolation Policy
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {metaPlatform.data_isolation_policy}
                  </span>
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Max Platforms</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {metaPlatform.max_platforms}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Support Email</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {metaPlatform.support_email || 'Not set'}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Support Phone</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {metaPlatform.support_phone || 'Not set'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Features */}
      {metaPlatform && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Features</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Enabled capabilities for this meta-platform
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <span
                  className={`h-4 w-4 rounded-full mr-2 ${
                    metaPlatform.white_label_enabled
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
                <span className="text-sm text-gray-900">White Label</span>
              </div>
              <div className="flex items-center">
                <span
                  className={`h-4 w-4 rounded-full mr-2 ${
                    metaPlatform.custom_pricing_enabled
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
                <span className="text-sm text-gray-900">Custom Pricing</span>
              </div>
              <div className="flex items-center">
                <span
                  className={`h-4 w-4 rounded-full mr-2 ${
                    metaPlatform.feature_gating_enabled
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
                <span className="text-sm text-gray-900">Feature Gating</span>
              </div>
              <div className="flex items-center">
                <span
                  className={`h-4 w-4 rounded-full mr-2 ${
                    metaPlatform.quota_management_enabled
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
                <span className="text-sm text-gray-900">Quota Management</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Health */}
      {health && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              API Health Status
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Current status of backend services
            </p>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Service</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {health.service}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      health.status === 'healthy'
                        ? 'bg-green-100 text-green-800'
                        : health.status === 'degraded'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {health.status}
                  </span>
                </dd>
              </div>
              {health.databases && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Databases</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span
                          className={`h-2 w-2 rounded-full mr-2 ${
                            health.databases.payments === 'healthy'
                              ? 'bg-green-500'
                              : 'bg-red-500'
                          }`}
                        />
                        <span>Payments: {health.databases.payments}</span>
                      </div>
                      <div className="flex items-center">
                        <span
                          className={`h-2 w-2 rounded-full mr-2 ${
                            health.databases.saas === 'healthy'
                              ? 'bg-green-500'
                              : 'bg-red-500'
                          }`}
                        />
                        <span>SaaS: {health.databases.saas}</span>
                      </div>
                    </div>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
